'use client';

import { submitHostForm } from '@/lib/supabase/form-actions'
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { hostFormSchema, type HostFormData } from '@/lib/types/forms';
import { captureAnalyticsEvent } from '@/lib/analytics';
import { buildHostWhatsAppMessage, buildWhatsAppUrl } from '@/lib/links';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface HostFormDialogProps {
  trigger: React.ReactNode;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  appearance?: 'default' | 'homepage';
}

export function HostFormDialog({ trigger, onSuccess, onError, appearance = 'default' }: HostFormDialogProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isHomepage = appearance === 'homepage';

  const fieldClassName = isHomepage
    ? 'h-11 rounded-xl border-input bg-background text-foreground shadow-none placeholder:text-muted-foreground/60 focus-visible:border-brand focus-visible:ring-brand/20'
    : undefined;
  const labelClassName = isHomepage
    ? 'text-[0.62rem] font-semibold uppercase tracking-[0.15em] text-muted-foreground'
    : undefined;
  const messageClassName = isHomepage ? 'text-destructive' : undefined;
  const selectItemClassName = isHomepage
    ? 'text-foreground focus:bg-accent focus:text-accent-foreground [&_svg:not([class*=text-])]:text-brand'
    : undefined;

  const form = useForm<HostFormData>({
    resolver: zodResolver(hostFormSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      district: '',
    },
  });

  const onSubmit = async (data: HostFormData) => {
    captureAnalyticsEvent('inquiry_submitted', { inquiry_type: 'host' });
    setIsSubmitting(true);
    try {
      await submitHostForm(data);
      captureAnalyticsEvent('inquiry_submission_succeeded', { inquiry_type: 'host' });
      captureAnalyticsEvent('booking_intent_clicked', { inquiry_type: 'host', channel: 'whatsapp' });
      window.open(
        buildWhatsAppUrl(buildHostWhatsAppMessage(data)),
        '_blank',
        'noopener,noreferrer',
      );
      toast.success('Opening WhatsApp', {
        description: 'Your application is saved — send the message to confirm with us.',
      });
      setOpen(false);
      form.reset();
      onSuccess?.();
    } catch (error) {
      captureAnalyticsEvent('inquiry_submission_failed', { inquiry_type: 'host' });
      onError?.(error as Error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && !open) {
      captureAnalyticsEvent('inquiry_opened', { inquiry_type: 'host' });
    }
    setOpen(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        overlayClassName={isHomepage ? 'bg-primary/55 backdrop-blur-sm' : undefined}
        className={cn(
          'max-h-[90vh] max-w-md overflow-y-auto',
          isHomepage && [
            'oorva-site-theme gap-0 rounded-[1.5rem] border-border bg-card p-0 text-foreground shadow-[0_28px_80px_rgba(16,46,38,0.24)]',
            '[&_[data-slot=dialog-close]]:grid [&_[data-slot=dialog-close]]:size-10 [&_[data-slot=dialog-close]]:place-items-center [&_[data-slot=dialog-close]]:rounded-full',
            '[&_[data-slot=dialog-close]]:text-muted-foreground [&_[data-slot=dialog-close]]:hover:bg-secondary [&_[data-slot=dialog-close]]:hover:text-foreground',
            '[&_[data-slot=dialog-close]]:ring-offset-card [&_[data-slot=dialog-close]]:focus:ring-brand',
          ],
        )}
      >
        <DialogHeader className={cn(isHomepage && 'gap-2 border-b border-border px-5 pb-4 pt-5 text-left')}>
          {isHomepage && (
            <p className="text-[0.58rem] font-semibold uppercase tracking-[0.22em] text-accent-foreground">
              Host with Oorva
            </p>
          )}
          <DialogTitle className={cn(isHomepage && 'font-sans text-[1.65rem] font-semibold leading-tight tracking-[-0.025em] text-foreground')}>
            Become a Host
          </DialogTitle>
          <DialogDescription className={cn(isHomepage && 'text-sm leading-5 text-muted-foreground')}>
            Share your craft, culture, or local story with curious travellers.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className={cn('space-y-4', isHomepage && 'px-5 py-5')}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClassName}>Name *</FormLabel>
                  <FormControl>
                    <Input className={fieldClassName} placeholder="Enter your name" {...field} />
                  </FormControl>
                  <FormMessage className={messageClassName} />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClassName}>Email</FormLabel>
                  <FormControl>
                    <Input className={fieldClassName} type="email" placeholder="your@email.com" {...field} />
                  </FormControl>
                  <FormMessage className={messageClassName} />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClassName}>Phone</FormLabel>
                  <FormControl>
                    <Input className={fieldClassName} type="tel" placeholder="Enter phone number" {...field} />
                  </FormControl>
                  <FormMessage className={messageClassName} />
                </FormItem>
              )}
            />

            <p className="-mt-2 text-xs text-muted-foreground">
              * Provide at least one contact method (email or phone)
            </p>

            <FormField
              control={form.control}
              name="district"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClassName}>District *</FormLabel>
                  <FormControl>
                    <Input className={fieldClassName} placeholder="Enter your district" {...field} />
                  </FormControl>
                  <FormMessage className={messageClassName} />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClassName}>Category *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger
                        className={cn(
                          'w-full',
                          isHomepage && 'h-11 rounded-xl border-input bg-background text-foreground shadow-none data-[placeholder]:text-muted-foreground/60 focus-visible:border-brand focus-visible:ring-brand/20 [&_svg:not([class*=text-])]:text-muted-foreground',
                        )}
                      >
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className={cn(isHomepage && 'oorva-site-theme border-border bg-card text-foreground shadow-2xl')}>
                      <SelectItem className={selectItemClassName} value="art_craft">Art & Craft</SelectItem>
                      <SelectItem className={selectItemClassName} value="agriculture">Agriculture</SelectItem>
                      <SelectItem className={selectItemClassName} value="food">Food</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage className={messageClassName} />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className={cn(
                'w-full',
                isHomepage && 'h-11 rounded-full bg-brand text-white shadow-none hover:bg-brand-hover focus-visible:border-brand focus-visible:ring-brand/30',
              )}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
